import { CreditCard } from "lucide-react";
import { formatCurrency, monthsToYears, formatSegmentLine } from "@/lib/amortize";

// Per-card payoff breakdown table (responsive: horizontal scroll on small screens).
export const CardBreakdownTable = ({ cards }) => {
  if (!cards || cards.length === 0) return null;

  return (
    <div className="bg-white rounded-3xl shadow-[0_8px_30px_rgb(0,0,0,0.04)] border border-slate-100 p-6 md:p-8" data-testid="card-breakdown">
      <div className="flex items-center gap-2 text-slate-400 mb-5">
        <CreditCard className="w-4 h-4" />
        <span className="text-xs font-bold uppercase tracking-widest">Per-card breakdown</span>
      </div>

      <div className="overflow-x-auto -mx-2 px-2">
        <table className="w-full text-sm border-collapse">
          <thead>
            <tr className="text-left text-xs font-bold text-slate-500 uppercase tracking-widest border-b border-slate-200">
              <th className="py-3 pr-4 whitespace-nowrap">Credit card</th>
              <th className="py-3 px-4 whitespace-nowrap">Payoff length</th>
              <th className="py-3 px-4 text-right whitespace-nowrap">Total interest</th>
              <th className="py-3 px-4 text-right whitespace-nowrap">Total payments</th>
              <th className="py-3 pl-4 min-w-[240px]">Payment schedule</th>
            </tr>
          </thead>
          <tbody>
            {cards.map((c, i) => (
              <tr key={c.id ?? i} className="border-b border-slate-100 last:border-0 align-top" data-testid={`card-row-${i}`}>
                <td className="py-4 pr-4 font-semibold text-slate-800 whitespace-nowrap">{c.name}</td>
                <td className="py-4 px-4 text-slate-700 whitespace-nowrap" data-testid={`card-months-${i}`}>
                  {c.neverPayoff ? (
                    <span className="text-red-600 font-medium">Never</span>
                  ) : (
                    <>
                      <span className="font-medium tabular-nums">{c.months} mo</span>
                      <div className="text-xs text-slate-400">{monthsToYears(c.months)}</div>
                    </>
                  )}
                </td>
                <td className="py-4 px-4 text-right tabular-nums text-slate-700 whitespace-nowrap" data-testid={`card-interest-${i}`}>
                  {c.neverPayoff ? "—" : formatCurrency(c.totalInterest, 2)}
                </td>
                <td className="py-4 px-4 text-right tabular-nums text-slate-700 whitespace-nowrap" data-testid={`card-total-${i}`}>
                  {c.neverPayoff ? "—" : formatCurrency(c.totalPaid, 2)}
                </td>
                <td className="py-4 pl-4 text-slate-600 leading-relaxed min-w-[240px]" data-testid={`card-schedule-${i}`}>
                  {c.neverPayoff ? (
                    <span className="text-red-600">Payments don't cover the interest — this card never pays off.</span>
                  ) : (
                    c.segments.map((seg, k) => (
                      <div key={seg.start} className="tabular-nums">{formatSegmentLine(seg, k, c.segments.length)}</div>
                    ))
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};
